import axios from 'axios'
import qs from 'querystring'
import { isExpired } from 'lib/token-utils'
import { getWafToken, isWafEnabled } from 'lib/aws-waf'
import { requestCaptchaSolve } from 'lib/waf-bridge'
import { readApiToken, setApiToken, clearUserToken, ANON_API_TOKEN_KEY } from 'lib/api-token'
import { getSessionStorage } from 'lib/get-session-storage'
import { notifyAuthInvalidated } from 'lib/auth-bridge'

const IS_DEV = process.env.NODE_ENV === 'development'
const IS_SERVER = typeof window === 'undefined'
const extraHeaders = {}
//
// MILO:
//       In the interest of my RAM I run without Kong
//       in dev, so we can spoof our credentials...
//       Note also the API won't mint a token without
//       verifying the Kong consumer exists so you have
//       to set one manually in localStorage (at 'apiToken')
//
if (IS_DEV) {
  extraHeaders['x-consumer-id'] = 'FAKE BOY'
  extraHeaders['x-consumer-name'] = 'scitedevapi'
} else if (WEBPACK_SERVER_BUNDLE && IS_SERVER) {
  //
  // Server-side (SSR) calls reach FastAPI directly through the internal load
  // balancer, bypassing Kong; set the consumer headers Kong would normally add.
  // Gated on WEBPACK_SERVER_BUNDLE (a build-time constant, unlike IS_SERVER)
  // so Terser can prove this branch dead and strip it — and the CONFIG.* reads
  // inside it — from the client bundle instead of leaving unreachable-but-present
  // code that still gets a CONFIG literal inlined at build time (SCI-3922).
  extraHeaders['x-consumer-id'] = CONFIG.apiBackendConsumer.id
  extraHeaders['x-consumer-name'] = CONFIG.apiBackendConsumer.name
}

export const getUrl = () => {
  if (IS_DEV && !IS_SERVER) {
    return `http://${window.location.hostname}:8000`
  }

  //
  // Server-side (SSR) the Node backend reaches FastAPI directly through the
  // internal load balancer, bypassing CloudFront/Kong. The browser keeps using
  // the public apiURL. Gated on WEBPACK_SERVER_BUNDLE too (see above) so this
  // branch — and its CONFIG.apiInternalURL read — is dead code in the client
  // bundle, not just unreachable at runtime.
  //
  if (WEBPACK_SERVER_BUNDLE && IS_SERVER) {
    return CONFIG.apiInternalURL || CONFIG.apiURL
  }

  return CONFIG.apiURL
}

export const api = axios.create({
  baseURL: getUrl(),
  paramsSerializer: params => qs.stringify(params),
  headers: { ...extraHeaders }
})

export const webApi = axios.create({
  baseURL: IS_SERVER ? 'http://localhost:3000/api' : '/api',
  headers: { ...extraHeaders }
})

//
// Authorization is attached per request, never as a default header, so a
// request with no usable token goes out with no header at all. `Bearer null`,
// `Bearer undefined` and a bare `Bearer` are exactly what locked users out
// (SCI-3965). `anonymous: true` on a request config omits the header even
// when a token exists: the login lookups must never fail because of a login
// credential.
//
// An explicit, non-blank bearer the caller already put on `config.headers`
// (client/store/report/sagas.js builds one from the anonymous-report session
// token) is authoritative and is left untouched — `resolveToken` is not even
// called, so it can't clobber it with the logged-in user's own token or clear
// it because none is stored. `anonymous: true` still wins over an explicit
// header: anonymous means no credential at all.
//
// Both spellings: lib/api/client.ts rebuilds headers from a WHATWG Request,
// which lowercases them, so an openapi-fetch caller's credential arrives as
// `authorization` and must be seen — and dropped — just like `Authorization`.
const authorizationHeader = config => config.headers?.Authorization ?? config.headers?.authorization

const deleteAuthorization = config => {
  delete config.headers.Authorization
  delete config.headers.authorization
}

const hasExplicitCredential = config => {
  const value = authorizationHeader(config)
  return typeof value === 'string' && /^Bearer\s+(?!(null|undefined)$)\S+$/.test(value.trim())
}

const withAuthorization = async (config, resolveToken) => {
  if (config.anonymous) {
    deleteAuthorization(config)
    return config
  }
  if (hasExplicitCredential(config)) {
    return config
  }
  const token = await resolveToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else {
    deleteAuthorization(config)
  }
  return config
}

let tokenRefreshing = null

const refreshIfExpired = async token => {
  if (!isExpired(token)) {
    return token
  }
  if (!tokenRefreshing) {
    tokenRefreshing = webApi.get('/auth/api_token')
  }
  try {
    const { data: newToken } = await tokenRefreshing
    return setApiToken(newToken) ? readApiToken() : null
  } catch (e) {
    setApiToken(null)
    throw e
  } finally {
    tokenRefreshing = null
  }
}

//
// Refresh the user's API token if it has expired, then return the current one.
// Shared so callers outside the axios instances (e.g. the assistant-ui data-stream
// runtime, which sets its own Authorization header) go through the same
// expiry-check-and-refresh path instead of reading a possibly-stale token
// directly. Returns null server-side or when no token is stored; a refresh
// failure propagates so the caller fails the request rather than sending it bare.
//
export const ensureFreshApiToken = async () => {
  if (IS_SERVER) {
    return null
  }
  const token = readApiToken()
  return token ? refreshIfExpired(token) : null
}

//
// Check before we send a request if we need to refresh the user's API token,
// then attach whatever credential the request should carry.
//
api.interceptors.request.use(config =>
  withAuthorization(config, async () => {
    if (IS_SERVER) {
      //
      // MILO: This is kind of a hack and I apologize
      //       but seems less likely to accidentally leak the secret
      //       into the client bundle than using eg config.get()
      //
      return global.SCITE_API_JWT_TOKEN_BACKEND
    }
    const token = readApiToken()
    return token && (await refreshIfExpired(token))
  })
)

webApi.interceptors.request.use(config => withAuthorization(config, () => (IS_SERVER ? null : readApiToken())))

//
// Attach the AWS WAF token to every browser request so AWS WAF Bot Control can
// evaluate it at the edge. No-ops server-side: SSR traffic reaches FastAPI
// through the internal load balancer, behind the WAF entirely.
//
// A missing token is deliberately not fatal: the WAF starts out in COUNT mode,
// so the request goes out bare and the edge decides. getWafToken has already
// surfaced a one-time toast in that case. Revisit if we ever move to BLOCK.
//
const attachWafToken = async config => {
  // isWafEnabled() is false server-side, so this covers SSR too.
  if (!isWafEnabled()) {
    return config
  }

  const token = await getWafToken()
  if (token) {
    config.headers['x-aws-waf-token'] = token
  }

  return config
}

api.interceptors.request.use(attachWafToken)
webApi.interceptors.request.use(attachWafToken)

//
// AWS WAF answers a blocked request differently depending on the rule action:
// CAPTCHA sends HTTP 405 and needs the interactive puzzle solved before a retry
// can succeed. Challenge sends HTTP 202 and is solved silently - the retried
// request re-enters the request interceptor chain below, so attachWafToken
// fetches a fresh token on its own; no separate solve step needed here. Both
// need the WAF custom response to send CORS headers for cross-origin (api
// host) calls, otherwise the browser cannot read the response and neither
// branch fires.
//
// `onGiveUp` supplies the original outcome when the user dismisses the CAPTCHA
// puzzle. It's passed as `then`'s second argument rather than a trailing
// `.catch` so it only covers requestCaptchaSolve - a genuine failure from the
// retried request still propagates instead of being masked by the original 405.
//
// A retried request can still come back challenged (the token didn't land in
// time, or the WAF SDK never loaded). The retry-once guard below must not let
// that second 405/202 fall through as if it were a real response: both have an
// empty body, and a caller that destructures real fields out of it crashes
// instead of failing cleanly.
const isWafChallengeStatus = status => status === 405 || status === 202

const retryAfterWafChallenge = (client, response, config, onGiveUp) => {
  if (!response || !config) {
    return null
  }

  if (config.__wafChallengeRetried) {
    if (isWafChallengeStatus(response.status)) {
      return Promise.reject(Object.assign(new Error('AWS WAF challenge unresolved after retry'), { response, config }))
    }
    return null
  }

  if (response.status === 405) {
    config.__wafChallengeRetried = true
    return requestCaptchaSolve().then(() => client(config), onGiveUp)
  }

  if (response.status === 202) {
    config.__wafChallengeRetried = true
    return client(config)
  }

  return null
}

//
// Registered on both the fulfilled and rejected handlers on purpose. Callers
// that set `validateStatus` — lib/api/client.ts does, so openapi-fetch can map
// statuses itself — make axios *resolve* on a 405/202, so a rejected-only
// handler would silently skip the challenge for them.
//
// Two ways this can still end unresolved, handled differently:
// - The puzzle is dismissed: gives up back into the caller's own contract - the
//   fulfilled side resolves with the original response (a caller that opted out
//   of throwing must not start throwing), the rejected side re-rejects with the
//   original error (the caller cares that its request failed, not that a puzzle
//   was dismissed).
// - The retry itself comes back challenged again: both sides reject with a
//   synthetic error instead, since an empty challenge response has no real data
//   for either kind of caller to fall back on.
//
const installWafChallengeRetry = client => {
  client.interceptors.response.use(
    response => retryAfterWafChallenge(client, response, response.config, () => response) || response,
    error => {
      const giveUp = () => Promise.reject(error)
      return retryAfterWafChallenge(client, error.response, error.config, giveUp) || giveUp()
    }
  )
}

const INVALID_TOKEN_DETAIL = 'Invalid authorization token'

//
// FastAPI answers an unverifiable or malformed credential with
// 401 + `WWW-Authenticate: Bearer error="invalid_token"` (SCI-3965); the body
// detail is matched as well for callers that can read it. `lib/api/client.ts`
// calls with `responseType: 'arraybuffer'`, so for those callers
// `response.data?.detail` is always undefined and only the `www-authenticate`
// branch can fire.
//
const isInvalidTokenResponse = response => {
  if (response?.status !== 401) {
    return false
  }
  return (
    /invalid_token/.test(response.headers?.['www-authenticate'] || '') || response.data?.detail === INVALID_TOKEN_DETAIL
  )
}

//
// Heal only the credential the server actually rejected, then send the request
// again bare. Almost every call succeeds anonymously on retry, so the user
// lands on a logged-out page instead of a dead one. Guarded like the WAF retry
// so a second rejection cannot loop, and only for requests that carried a
// credential. The retried config's own Authorization header is deleted here
// (not left for the request interceptor to reattach) so a rejected explicit
// header can't survive the retry the way `hasExplicitCredential` protects it.
//
// Which credential matters: a stale `?authToken=` from a shared report link is
// sent as an explicit header (client/store/report/sagas.js), and wiping the
// logged-in user's own apiToken over it would log them out of a link they just
// opened. So the user's apiToken is cleared, and the store told, only when the
// stored token is the one that was rejected; a rejected anon report token just
// drops out of sessionStorage; a header matching neither (storage already moved
// on) clears nothing.
//
// The retry is marked anonymous so it goes out bare whatever storage holds by
// then. Re-sending a request that carried one credential under a different one
// would misattribute the answer: the report saga reads `hasAccess` off the
// check_access response and would pin the stale share token as its override.
// Bare, the check answers as it did on master when the token failed, and the
// saga falls back to the default header on its own.
//
const retryWithoutCredential = (client, response, config) => {
  const rejected = config && authorizationHeader(config)
  if (IS_SERVER || !config || config.__authRetried || !rejected) {
    return null
  }
  if (!isInvalidTokenResponse(response)) {
    return null
  }
  config.__authRetried = true
  deleteAuthorization(config)

  const storedToken = readApiToken()
  const anonToken = getSessionStorage()?.getItem(ANON_API_TOKEN_KEY)
  if (storedToken && rejected === `Bearer ${storedToken}`) {
    clearUserToken()
    notifyAuthInvalidated()
  } else if (anonToken && rejected === `Bearer ${anonToken}`) {
    getSessionStorage()?.removeItem(ANON_API_TOKEN_KEY)
  }

  config.anonymous = true
  return client(config)
}

const installInvalidTokenRetry = client => {
  client.interceptors.response.use(
    response => retryWithoutCredential(client, response, response.config) || response,
    error => retryWithoutCredential(client, error.response, error.config) || Promise.reject(error)
  )
}

installInvalidTokenRetry(api)
installInvalidTokenRetry(webApi)
installWafChallengeRetry(api)
installWafChallengeRetry(webApi)

export default api
